import {useState, useEffect} from "react";
import {Settings, MapPin, Plus, X, Save, Trash2, AlertTriangle} from "lucide-react";
import {useLocation, useNavigate} from "react-router-dom";
import Header from "../components/Header";
import {saveConfigHandle, getConfigHandle, updateConfigHandle, getConfigByStoreHash} from "../services/configService";
import {
    saveLocationHandle,
    deleteLocationHandle,
    getLocationsUsingConfig
} from "../services/locationService";
import {useAppConfig} from "../context/AppConfigContext";

const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === "") {
        return {isValid: false, message: `${fieldName} is required`};
    }
    return {isValid: true, message: ""};
};

export default function ConfigManagement() {
    const [activeTab, setActiveTab] = useState("config");
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const navigate = useNavigate();
    const {state}= useLocation();
    const { appConfig, setAppConfig } = useAppConfig();
    const { savedConfig, stockData, storeHash, savedLocations } = appConfig;

    const [draftConfig, setDraftConfig] = useState(savedConfig || {
        id: appConfig.savedConfig?.id || null,
        storeHash: storeHash || "",
        fileType: "",
        fileUrl: "",
    });
    const [configErrors, setConfigErrors] = useState({});
    const [isEditingConfig, setIsEditingConfig] = useState(false);

    const [locations, setLocations] = useState(savedLocations || []);
    const [currentLocation, setCurrentLocation] = useState({name: "", locationId: ""});
    const [locationErrors, setLocationErrors] = useState({});

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [locationToDelete, setLocationToDelete] = useState(null);

    const handleConfigChange = (field, value) => {
        setDraftConfig({ ...draftConfig, [field]: value });
        const validation = validateRequired(value, field);
        setConfigErrors({...configErrors, [field]: validation.isValid ? "" : validation.message});
    };

    const handleSaveConfig = async () => {
        const requiredFields = ["storeHash", "fileType", "fileUrl"];
        const errors = {};
        requiredFields.forEach(field => {
            const validation = validateRequired(draftConfig[field], field);
            if (!validation.isValid) errors[field] = validation.message;
        });

        if (Object.keys(errors).length > 0) {
            setConfigErrors(errors);
            return;
        }

        setIsLoading(true);
        try {
            let res;
            if (draftConfig.id) {
                res = await updateConfigHandle(draftConfig.id, {
                    fileType: draftConfig.fileType,
                    fileUrl: draftConfig.fileUrl
                });
            } else {
                res = await saveConfigHandle({
                    storeHash: draftConfig.storeHash,
                    fileType: draftConfig.fileType,
                    fileUrl: draftConfig.fileUrl
                });
            }

            if (res) {
                const updated = { ...draftConfig, id: res.id };
                setAppConfig({ ...appConfig, savedConfig: updated });

                setMessage("Configuration saved successfully!");
                setIsEditingConfig(false);
                setTimeout(() => setMessage(""), 3000);
            } else {
                setMessage("Failed to save configuration");
            }
        } catch (error) {
            console.error('Error saving config:', error);
            setMessage("Network error. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleLocationChange = (field, value) => {
        setCurrentLocation({...currentLocation, [field]: value});
        const validation = validateRequired(value, field);
        setLocationErrors({...locationErrors, [field]: validation.isValid ? "" : validation.message});
    };

    const handleAddLocation = async () => {
        if (!savedConfig.id) {
            setMessage("Please save configuration first before adding locations");
            return;
        }

        const errors = {};
        Object.keys(currentLocation).forEach(field => {
            const validation = validateRequired(currentLocation[field], field);
            if (!validation.isValid) errors[field] = validation.message;
        });

        if (Object.keys(errors).length > 0) {
            setLocationErrors(errors);
            return;
        }

        setIsLoading(true);
        try {
            const locationData = {
                ...currentLocation,
                appConfig: `/api/app_configs/${savedConfig.id}`,
            };
            const res = await saveLocationHandle(locationData);

            if (res) {
                const newLocations = [...locations, { ...currentLocation, id: res.id }];
                setLocations(newLocations);
                setAppConfig({ ...appConfig, locations: newLocations });
                setCurrentLocation({name: "", locationId: ""});
                setLocationErrors({});
                setMessage("Location added successfully!");
                setTimeout(() => setMessage(""), 3000);
            } else {
                setMessage("Failed to add location");
            }
        } catch (error) {
            console.error('Error adding location:', error);
            setMessage("Network error. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteLocation = async (locationId) => {
        setIsLoading(true);
        try {
            const success = await deleteLocationHandle(locationId);
            if (success) {
                const newLocations = locations.filter((loc) => loc.id !== locationId);
                setLocations(newLocations);

                setAppConfig({ ...appConfig, locations: newLocations });

                setMessage("Location deleted successfully!");
                setTimeout(() => setMessage(""), 3000);
            } else {
                setMessage("Failed to delete location");
            }
        } catch (error) {
            console.error('Error deleting location:', error);
            setMessage("Network error. Please try again.");
        } finally {
            setIsLoading(false);
            setShowDeleteModal(false);
            setLocationToDelete(null);
        }
    };

    const openDeleteModal = (location) => {
        setLocationToDelete(location);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setLocationToDelete(null);
    };

    const isConfigFormValid =
        !Object.values(configErrors).some(Boolean) &&
        draftConfig.storeHash &&
        draftConfig.fileType &&
        draftConfig.fileUrl;

    const isLocationFormValid =
        !Object.values(locationErrors).some(Boolean) &&
        currentLocation.name &&
        currentLocation.locationId;


    return (
        <div className="min-h-screen bg-black">
            <Header/>
            <main className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-white mb-2">Configuration Management</h1>
                    <p className="text-gray-400">Manage your app configuration and locations</p>
                </div>

                {/* Tab Navigation */}
                <div className="mb-8">
                    <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg w-fit">
                        <button
                            onClick={() => setActiveTab("config")}
                            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                activeTab === "config"
                                    ? "bg-[#c4fa00] text-black"
                                    : "text-gray-300 hover:text-white"
                            }`}
                        >
                            <Settings size={16} className="mr-2"/>
                            App Configuration
                        </button>
                        <button
                            onClick={() => setActiveTab("locations")}
                            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                                activeTab === "locations"
                                    ? "bg-[#c4fa00] text-black"
                                    : "text-gray-300 hover:text-white"
                            }`}
                        >
                            <MapPin size={16} className="mr-2"/>
                            Locations ({locations.length})
                        </button>
                    </div>
                </div>

                <div className="bg-gray-900 rounded-lg border border-gray-800">
                    {/* Configuration Tab */}
                    {activeTab === "config" && (
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-semibold text-white">App Configuration</h2>
                                {savedConfig.id && !isEditingConfig && (
                                    <button
                                        onClick={() => setIsEditingConfig(true)}
                                        className="px-4 py-2 bg-[#c4fa00] hover:bg-[#e0ff80] text-black font-medium rounded-lg"
                                    >
                                        Edit Configuration
                                    </button>
                                )}
                            </div>

                            {(!savedConfig.id || isEditingConfig) ? (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {/*<div>*/}
                                        {/*    <label className="block text-sm font-medium text-gray-300 mb-2">*/}
                                        {/*        Store Hash*/}
                                        {/*    </label>*/}
                                        {/*    <input*/}
                                        {/*        type="text"*/}
                                        {/*        placeholder="Enter Store Hash"*/}
                                        {/*        value={config.storeHash}*/}
                                        {/*        onChange={(e) => handleConfigChange("storeHash", e.target.value)}*/}
                                        {/*        className={`w-full px-4 py-3 bg-gray-800 border ${*/}
                                        {/*            configErrors.storeHash ? 'border-red-500' : 'border-gray-700'*/}
                                        {/*        } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400`}*/}
                                        {/*        disabled*/}
                                        {/*    />*/}
                                        {/*    {configErrors.storeHash && (*/}
                                        {/*        <p className="mt-2 text-sm text-red-400">{configErrors.storeHash}</p>*/}
                                        {/*    )}*/}
                                        {/*</div>*/}

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                File URL
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Enter File URL"
                                                value={draftConfig.fileUrl}
                                                onChange={(e) => handleConfigChange("fileUrl", e.target.value)}
                                                className={`w-full px-4 py-3 bg-gray-800 border ${
                                                    configErrors.fileUrl ? 'border-red-500' : 'border-gray-700'
                                                } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400`}
                                            />
                                            {configErrors.fileUrl && (
                                                <p className="mt-2 text-sm text-red-400">{configErrors.fileUrl}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                File Type
                                            </label>
                                            <select
                                                value={draftConfig.fileType}
                                                onChange={(e) => handleConfigChange("fileType", e.target.value)}
                                                className={`w-full px-4 py-3 bg-gray-800 border ${
                                                    configErrors.fileType ? 'border-red-500' : 'border-gray-700'
                                                } rounded-lg text-white focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400`}
                                            >
                                                <option value="">Select File Type</option>
                                                {['csv', 'xlsx'].map(type => (
                                                    <option key={type} value={type}>{type.toUpperCase()}</option>
                                                ))}
                                            </select>
                                            {configErrors.fileType && (
                                                <p className="mt-2 text-sm text-red-400">{configErrors.fileType}</p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex space-x-4">
                                        <button
                                            onClick={handleSaveConfig}
                                            disabled={!isConfigFormValid || isLoading}
                                            className="flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-semibold rounded-lg"
                                        >
                                            <Save size={16} className="mr-2"/>
                                            {isLoading ? "Saving..." : (draftConfig.id ? "Update Configuration" : "Save Configuration")}
                                        </button>

                                        {draftConfig.id && isEditingConfig && (
                                            <button
                                                onClick={() => {
                                                    setIsEditingConfig(false);
                                                    setConfigErrors({});
                                                }}
                                                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {/*<div>*/}
                                        {/*    <label className="block text-sm font-medium text-gray-400 mb-1">Store*/}
                                        {/*        Hash</label>*/}
                                        {/*    <p className="text-white">{config.storeHash}</p>*/}
                                        {/*</div>*/}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">File
                                                URL</label>
                                            <p className="text-white break-all">{savedConfig.fileUrl}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">File
                                                Type</label>
                                            <p className="text-white uppercase">{savedConfig.fileType}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Locations Tab */}
                    {activeTab === "locations" && (
                        <div className="p-6">
                            <h2 className="text-xl font-semibold text-white mb-6">Manage Locations</h2>

                            {!savedConfig.id && (
                                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
                                    <p className="text-red-400">Please save your app configuration first before managing
                                        locations.</p>
                                </div>
                            )}

                            {/* Add Location Form */}
                            <div className="bg-gray-800 rounded-lg p-4 mb-6">
                                <h3 className="text-lg font-medium text-white mb-4">Add New Location</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            Location Name
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter Location Name"
                                            value={currentLocation.name}
                                            onChange={(e) => handleLocationChange("name", e.target.value)}
                                            className={`w-full px-4 py-3 bg-gray-700 border ${
                                                locationErrors.name ? 'border-red-500' : 'border-gray-600'
                                            } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400`}
                                        />
                                        {locationErrors.name && (
                                            <p className="mt-2 text-sm text-red-400">{locationErrors.name}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">
                                            BC ID
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter BC ID"
                                            value={currentLocation.locationId}
                                            onChange={(e) => handleLocationChange("locationId", e.target.value)}
                                            className={`w-full px-4 py-3 bg-gray-700 border ${
                                                locationErrors.locationId ? 'border-red-500' : 'border-gray-600'
                                            } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400`}
                                        />
                                        {locationErrors.locationId && (
                                            <p className="mt-2 text-sm text-red-400">{locationErrors.locationId}</p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={handleAddLocation}
                                    disabled={!isLocationFormValid || isLoading || !savedConfig.id}
                                    className="flex items-center px-6 py-3 bg-[#c4fa00] hover:bg-[#e0ff80] disabled:bg-gray-600 text-black font-semibold rounded-lg"
                                >
                                    <Plus size={16} className="mr-2"/>
                                    {isLoading ? "Adding..." : "Add Location"}
                                </button>
                            </div>

                            {/* Locations List */}
                            {locations.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-medium text-white mb-4">
                                        Your Locations ({locations.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {locations.map((location) => (
                                            <div
                                                key={location.id}
                                                className="flex justify-between items-center bg-gray-800 p-4 rounded-lg"
                                            >
                                                <div>
                                                    <p className="text-white font-medium">{location.name}</p>
                                                    <p className="text-gray-400 text-sm">BC
                                                        ID: {location.locationId}</p>
                                                </div>
                                                <button
                                                    onClick={() => openDeleteModal(location)}
                                                    className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                                                    disabled={isLoading}
                                                >
                                                    <Trash2 size={18}/>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {locations.length === 0 && savedConfig.id && (
                                <div className="text-center py-8">
                                    <MapPin size={48} className="mx-auto text-gray-600 mb-3"/>
                                    <p className="text-gray-400">No locations added yet. Add your first location
                                        above.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Message Display */}
                    {message && (
                        <div className="px-6 pb-6">
                            <p className={`text-sm ${
                                message.includes("successfully") ? "text-green-400" : "text-red-400"
                            }`}>
                                {message}
                            </p>
                        </div>
                    )}
                </div>
            </main>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && locationToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center mb-4">
                            <AlertTriangle size={24} className="text-red-400 mr-3"/>
                            <h3 className="text-lg font-semibold text-white">Confirm Delete</h3>
                        </div>
                        <p className="text-gray-300 mb-6">
                            Are you sure you want to delete the location <strong>"{locationToDelete.name}"</strong>?
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => handleDeleteLocation(locationToDelete.id)}
                                disabled={isLoading}
                                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white font-medium rounded-lg"
                            >
                                {isLoading ? "Deleting..." : "Delete"}
                            </button>
                            <button
                                onClick={closeDeleteModal}
                                disabled={isLoading}
                                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-medium rounded-lg"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
